import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { polyfill } from "mobile-drag-drop";
import { scrollBehaviourDragImageTranslateOverride } from "mobile-drag-drop/scroll-behaviour";
import "mobile-drag-drop/default.css";

polyfill({ dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride, holdToDrag: 250 });
window.addEventListener("touchmove", () => {}, { passive: false });

// Popups only close with their X: clicks on the dark area behind any popup (the full-screen backdrop itself)
// are ignored everywhere. A layer can opt out with data-outside-close (e.g. right-click menus).
const isBackdrop = (el) => el instanceof HTMLElement && el.classList.contains("fixed") && el.classList.contains("inset-0") && !el.closest("[data-outside-close]");
["mousedown", "click"].forEach((type) => window.addEventListener(type, (e) => { if (isBackdrop(e.target)) e.stopPropagation(); }, true));

// Enter moves to the next field in the same popup / form (like Tab). Fields that handle Enter themselves
// (search, barcode scan, keypad, "add" boxes — anything with its own key handler) are left alone,
// as are text areas and fields marked data-enter-keep. On the last field of a form Enter still submits.
const SKIP_TYPES = new Set(["button", "submit", "reset", "file", "image", "hidden", "range", "color"]);
const reactProps = (el) => { const k = Object.keys(el).find((x) => x.startsWith("__reactProps$")); return k ? el[k] : null; };
const isField = (el) => (el.tagName === "INPUT" && !SKIP_TYPES.has(el.type)) || el.tagName === "SELECT";
const visible = (el) => { const cs = getComputedStyle(el); return el.getClientRects().length > 0 && cs.visibility !== "hidden" && cs.opacity !== "0"; };
window.addEventListener("keydown", (e) => {
  const el = e.target;
  if (e.key !== "Enter" || e.defaultPrevented || e.isComposing || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
  if (!(el instanceof HTMLElement) || !isField(el) || el.closest("[data-enter-keep]")) return;
  const p = reactProps(el); if (p && (p.onKeyDown || p.onKeyPress || p.onKeyUp)) return;
  const scope = el.closest("form") || el.closest('[role="dialog"]') || el.closest(".fixed.inset-0") || document.body;
  const all = [...scope.querySelectorAll("input, select, textarea")].filter((x) => (isField(x) || x.tagName === "TEXTAREA") && !x.disabled && !x.readOnly && x.tabIndex !== -1 && visible(x));
  const next = all[all.indexOf(el) + 1];
  if (!next) return; // last field: let a form submit as normal
  e.preventDefault();
  next.focus();
  try { if (/^(text|search|email|tel|url|number|password)$/.test(next.type || "")) next.select(); } catch { /* not selectable */ }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
